import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FileText } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  categoryForecast, monthlyForecast, yearlyForecast,
} from "@/lib/api";
import { EUR, fmtMonth, num } from "@/lib/format";
import { ExportButtons } from "@/components/ExportButtons";
import { buildFilename } from "@/lib/export";
import { buildForecastPdfReport } from "@/lib/pdf-report";

export default function Forecast() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [monthly, setMonthly] = useState([]);
  const [byCategory, setByCategory] = useState([]);
  const [yearly, setYearly] = useState(0);
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const [m, c, y] = await Promise.all([
        monthlyForecast({ year }),
        categoryForecast({ year }),
        yearlyForecast({ year }),
      ]);
      setMonthly(m || []);
      setByCategory(c || []);
      setYearly(num(y?.total));
    })();
  }, [year]);

  const chartData = useMemo(
    () =>
      monthly.map((r) => ({
        month: fmtMonth(r.year, r.month),
        total: num(r.total),
        forecast: num(r.forecast_amount),
        adjustment: num(r.adjustment_amount),
      })),
    [monthly],
  );

  const peak = useMemo(() => {
    let best = null;
    for (const r of chartData) {
      if (!best || r.total > best.total) best = r;
    }
    return best;
  }, [chartData]);

  const avg = useMemo(() => {
    const nonZero = chartData.filter((r) => r.total > 0);
    if (nonZero.length === 0) return 0;
    return nonZero.reduce((s, r) => s + r.total, 0) / nonZero.length;
  }, [chartData]);

  const handleExportPdf = async () => {
    if (pdfBusy) return;
    setPdfBusy(true);
    try {
      await buildForecastPdfReport({
        year,
        monthly,
        byCategory,
        yearlyTotal: yearly,
        peak,
        monthlyAvg: avg,
      });
      toast.success(`Report PDF previsione ${year} generato.`);
    } catch (e) {
      toast.error("Errore nella generazione del PDF.");
      console.error(e);
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div data-testid="forecast-page">
      <PageHeader
        title="Previsione"
        subtitle="Aggregazione per competenza (due_date) delle spese attive e una tantum"
        actions={
          <div className="flex items-center gap-2">
            <Label htmlFor="year" className="text-xs uppercase tracking-wider text-stone-500">
              Anno
            </Label>
            <Input
              id="year"
              type="number"
              data-testid="input-year"
              className="w-24"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            />
            <Button
              data-testid="export-forecast-pdf"
              variant="outline"
              size="sm"
              onClick={handleExportPdf}
              disabled={pdfBusy || monthly.length === 0}
              title="Esporta report PDF con KPI, grafico e tabelle"
            >
              <FileText size={14} className="mr-1.5" />
              {pdfBusy ? "Generazione…" : "PDF"}
            </Button>
            <ExportButtons
              sheets={[
                {
                  name: "Mensile",
                  rows: monthly.map((r) => ({
                    month_label: fmtMonth(r.year, r.month),
                    year: r.year,
                    month: r.month,
                    forecast_amount: r.forecast_amount,
                    adjustment_amount: r.adjustment_amount,
                    total: r.total,
                  })),
                  columns: [
                    { key: "month_label", header: "Mese" },
                    { key: "year", header: "Anno", type: "integer" },
                    { key: "month", header: "Numero mese", type: "integer" },
                    { key: "forecast_amount", header: "Previsione (€)", type: "currency" },
                    { key: "adjustment_amount", header: "Aggiustamenti (€)", type: "currency" },
                    { key: "total", header: "Totale (€)", type: "currency" },
                  ],
                },
                {
                  name: "Per categoria",
                  rows: byCategory.map((c) => ({
                    category_name: c.category_name,
                    forecast_amount: c.forecast_amount,
                    share_pct: yearly > 0 ? num(c.forecast_amount) / yearly : 0,
                  })),
                  columns: [
                    { key: "category_name", header: "Categoria" },
                    { key: "forecast_amount", header: "Importo (€)", type: "currency" },
                    { key: "share_pct", header: "Quota %", type: "percent" },
                  ],
                },
              ]}
              filenameBase={buildFilename("previsione", { year })}
              testId="export-forecast"
            />
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <StatCard testId="stat-yearly" label="Totale annuo previsto" value={yearly} tone="info" />
        <StatCard testId="stat-monthly-avg" label="Media mensile" value={avg} />
        <StatCard
          testId="stat-peak"
          label="Picco mensile"
          value={peak?.total || 0}
          hint={peak?.month || "—"}
          tone="warning"
        />
      </div>

      <section className="mt-8 rounded-xl border border-stone-200 bg-white p-5">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-stone-600">
          Distribuzione mensile
        </h2>
        <div data-testid="forecast-chart" className="h-72 w-full">
          <ResponsiveContainer width="100%" height="100%" minHeight={280}>
            <BarChart data={chartData} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
              <XAxis dataKey="month" fontSize={11} stroke="#78716C" />
              <YAxis fontSize={11} stroke="#78716C" tickFormatter={(v) => `€${v}`} />
              <Tooltip
                formatter={(v) => EUR(v)}
                contentStyle={{ fontSize: 12 }}
              />
              <Bar dataKey="total" radius={[4, 4, 0, 0]}>
                {chartData.map((r, i) => (
                  <Cell
                    key={i}
                    fill={peak && r.month === peak.month ? "#B45309" : "#2A4B3C"}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-stone-200 bg-white">
          <div className="border-b border-stone-200 px-5 py-3 text-sm font-semibold uppercase tracking-wider text-stone-600">
            Previsione per mese
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Mese</TableHead>
                <TableHead className="text-right">Previsione</TableHead>
                <TableHead className="text-right">Aggiustamenti</TableHead>
                <TableHead className="text-right">Totale</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {monthly.map((r) => (
                <TableRow key={r.month} data-testid={`forecast-row-${r.month}`}>
                  <TableCell>{fmtMonth(r.year, r.month)}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">
                    {EUR(r.forecast_amount)}
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums text-stone-500">
                    {EUR(r.adjustment_amount)}
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums font-semibold">
                    {EUR(r.total)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="rounded-xl border border-stone-200 bg-white">
          <div className="border-b border-stone-200 px-5 py-3 text-sm font-semibold uppercase tracking-wider text-stone-600">
            Previsione per categoria
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Categoria</TableHead>
                <TableHead className="text-right">Importo</TableHead>
                <TableHead className="text-right">Quota %</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {byCategory.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} className="py-8 text-center text-sm text-stone-500">
                    Nessun dato.
                  </TableCell>
                </TableRow>
              )}
              {byCategory.map((c) => {
                const pct = yearly > 0 ? (num(c.forecast_amount) / yearly) * 100 : 0;
                return (
                  <TableRow key={c.category_id} data-testid={`cat-row-${c.category_id}`}>
                    <TableCell>{c.category_name}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {EUR(c.forecast_amount)}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-stone-500">
                      {pct.toFixed(1)}%
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </section>
    </div>
  );
}
